import { groupBy, sortBy } from 'lodash-es'
import type { UseQueryOptions } from '@tanstack/vue-query'
import { useQuery } from '@tanstack/vue-query'

type SearchQueryOptions<R = SearchResult> = Partial<
  Omit<UseQueryOptions<SearchResult>, 'select'>
> & {
  enabled?: MaybeRefOrGetter<boolean | undefined>
  select?(result: SearchResult, params: SearchApiParams): R
}

export function useRedesignSearchQuery<R = SearchResult>(
  query: MaybeRefOrGetter<SearchApiParams>,
  options: SearchQueryOptions<R> = {},
) {
  const fetch = useRequestFetch()
  const context = useSearchContext()
  const confData = useConfdata()
  const season = useSearchSeason(query)

  const params = useQueryParams(() => ({
    language: confData.language,
    currency: confData.currency,
    salesoffice: confData.salesoffice?.code,
    season: season.value,
    ...toValue(query),
  }))

  return useQuery({
    ...(options as Omit<typeof options, 'enabled' | 'select'>),
    placeholderData: (prev: SearchApiResult | undefined) => prev, // Keep previous data between requests
    enabled: computed(
      () => toValue(options.enabled) !== false && import.meta.client,
    ),
    queryKey: ['search-api', 'accomtrips', params] as const,
    queryFn({ queryKey: [_, __, q], signal }) {
      return fetch<SearchApiResult>('/search-api/v1/engines/accomtrips', {
        query: mapSearchApiParams(q),
        signal,
      })
    },
    select(result) {
      const mappedResult = mapSearchResult(context, result, toValue(params))

      return options.select
        ? options.select(mappedResult, toValue(query))
        : mappedResult
    },
  })
}

export function useRedesignMapSearchBoundsQuery(
  area: MaybeRefOrGetter<{ bounds: BoundsValue; zoom: ZoomValue }>,
  filters: MaybeRefOrGetter<MapSearchApiParams>,
  options?: Omit<SearchQueryOptions, 'select'>,
) {
  const settings = useSettings()

  const query = computed(() => ({
    ...toValue(filters),
    ...toValue(area).bounds,
    zoom: toValue(area).zoom,
    pagesize:
      toValue(area).zoom >= settings.mapPricesZoomLevel
        ? SEARCH_API_PAGESIZE_MAX
        : SEARCH_API_PAGESIZE_NONE,
    page: undefined,
    country: undefined,
    region: undefined,
    place: undefined,
  }))

  return useRedesignSearchQuery(query, {
    ...options,
    select: getResultMapItems,
  })
}

export function getResultMapItems(result: SearchResult): MapItem[] {
  const { docs } = result
  const { buckets = [] } = result._aggregations?.karte ?? {}

  return docs.length
    ? getMapItemsFromDocs(docs)
    : getMapItemsFromBuckets(buckets)
}

function getMapItemsFromDocs(docs: SearchResultDocument[]): MapItemAccom[] {
  return Object.values(
    groupBy(
      sortBy(docs, 'trip.price'),
      ({ coords: { lat, lon } }) => lat + lon,
    ),
  ).map((items) => ({
    key: items.map(({ code }) => code).join(),
    type: 'accommodation',
    count: items.length,
    cardinality: 1,
    coords: items[0].coords,
    docs: items,
  }))
}

function getMapItemsFromBuckets(buckets: SearchApiBucket[]): MapItemCluster[] {
  return buckets
    .filter(({ coords, bounds }) => !!coords?.lat && !!coords.lon && !!bounds)
    .map(({ key, count, cardinality, coords, bounds }) => ({
      key,
      type: 'cluster',
      count: count || 1,
      cardinality: cardinality || 1,
      coords: coords!,
      bounds: bounds!,
    }))
}
